import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ phone: "", dob: "", ssn: "", street: "", city: "", state: "", zip: "", goals: "", referred_by: "", start_score: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/portal/overview").then((r) => {
      const c = r.data.client;
      setDone(c.intake_completed);
      setForm((f) => ({
        ...f, phone: c.phone || "", dob: c.dob || "",
        street: c.address?.street || "", city: c.address?.city || "", state: c.address?.state || "", zip: c.address?.zip || "",
        goals: c.intake?.goals || "", referred_by: c.intake?.referred_by || "",
        start_score: c.scores?.start || "",
      }));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/intake", { ...form, start_score: Number(form.start_score) || 0 });
      toast.success("Intake form saved");
      setDone(true);
      navigate("/portal");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="portal-onboarding">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Intake form</h1>
        <p className="text-slate-600 mt-1">This helps us prepare accurate dispute letters. Bureaus verify identity against these details.</p>
      </div>
      {done && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> Intake completed — you can update it any time.
        </div>
      )}
      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} placeholder="(555) 555-0100" className="mt-1" data-testid="intake-phone-input" /></div>
          <div><Label>Date of birth</Label><Input type="date" value={form.dob} onChange={set("dob")} className="mt-1" data-testid="intake-dob-input" /></div>
        </div>
        <div>
          <Label>Social Security Number</Label>
          <Input value={form.ssn} onChange={set("ssn")} placeholder="•••-••-••••" className="mt-1" data-testid="intake-ssn-input" />
          <p className="text-xs text-slate-400 mt-1">Encrypted at rest. Only the last 4 digits are ever displayed.</p>
        </div>
        <div><Label>Street address</Label><Input value={form.street} onChange={set("street")} className="mt-1" data-testid="intake-street-input" /></div>
        <div className="grid grid-cols-3 gap-4">
          <div><Label>City</Label><Input value={form.city} onChange={set("city")} className="mt-1" data-testid="intake-city-input" /></div>
          <div><Label>State</Label><Input value={form.state} onChange={set("state")} maxLength={2} placeholder="GA" className="mt-1" data-testid="intake-state-input" /></div>
          <div><Label>ZIP</Label><Input value={form.zip} onChange={set("zip")} className="mt-1" data-testid="intake-zip-input" /></div>
        </div>
        <div><Label>What are your goals?</Label><Textarea value={form.goals} onChange={set("goals")} placeholder="e.g. Buy a home next year, qualify for a better auto rate…" className="mt-1" data-testid="intake-goals-input" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>How did you hear about us?</Label><Input value={form.referred_by} onChange={set("referred_by")} className="mt-1" data-testid="intake-referral-input" /></div>
          <div><Label>Current credit score (if known)</Label><Input type="number" value={form.start_score} onChange={set("start_score")} className="mt-1" data-testid="intake-score-input" /></div>
        </div>
        <Button type="submit" disabled={busy} data-testid="intake-submit-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">
          {busy ? "Saving…" : done ? "Update intake form" : "Submit intake form"}
        </Button>
      </form>
    </div>
  );
}
